import { cn } from "@/lib/utils";
import { ISO } from "@/content/illustrations";
import { IsoGrid } from "./Iso";
import { LocaleLink } from "./LocaleLink";
import { useT, useTNode } from "@/lib/i18n-dict";


export function PrimaryCta({
  to,
  children,
  className,
  search,
}: {
  to: string;
  children: React.ReactNode;
  className?: string;
  /** Optional query params forwarded to the target route. */
  search?: Record<string, string>;
}) {
  const label = useTNode(children);
  return (
    <LocaleLink
      to={to}
      {...(search ? { search } : {})}
      className={cn(
        "inline-flex items-center justify-center rounded-md bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground transition-opacity hover:opacity-90",
        className,
      )}
    >
      {label}
    </LocaleLink>
  );
}

export function GhostCta({
  to,
  children,
  className,
}: {
  to: string;
  children: React.ReactNode;
  className?: string;
}) {
  const label = useTNode(children);
  return (
    <LocaleLink
      to={to}
      className={cn(
        "inline-flex items-center justify-center rounded-md border border-border px-6 py-3 text-sm font-semibold transition-colors hover:bg-accent",
        className,
      )}
    >
      {label}
    </LocaleLink>
  );
}

/**
 * Unified "demo or consultation" CTA band — shared by Pricing and the
 * Add-ons hub. Isometric illustration + short value bullets + two actions.
 */
export function ConsultCta({
  title = "Still running your plant on Excel and gut feeling?",
  body = "Every day of chaos costs you late orders, tied-up cash and endless fire-fighting. Book a 30-minute call — we map your shop-floor pain to factory.online and show exactly where it stops hurting. No obligation.",
  bullets = [
    "Live demo on your production scenario — not slideware",
    "A rollout plan with concrete timing and cost for your team size",
    "An honest answer if factory.online is not a fit",
  ],
  illustration = ISO.teamwork,
}: {
  title?: string;
  body?: string;
  bullets?: string[];
  illustration?: string;
}) {
  const t = useT();
  return (
    <section className="relative isolate border-t border-border/60 py-20 md:py-28">
      <IsoGrid intensity="soft" />
      <div className="container-page">
        <div className="iso-plate relative overflow-hidden rounded-2xl border border-primary/30 bg-surface p-8 shadow-[var(--glow-primary)] md:p-14">
          <div className="grid items-center gap-10 md:grid-cols-[1.15fr_1fr]">
            <div>
              <h2 className="font-display text-3xl font-bold text-balance-tight md:text-4xl">
                {t(title)}
              </h2>
              <p className="mt-4 max-w-xl text-base text-muted-foreground md:text-lg">{t(body)}</p>
              <ul className="mt-6 space-y-3">
                {bullets.map((b) => (
                  <li key={b} className="flex items-start gap-3 text-base md:text-lg">
                    <span
                      aria-hidden
                      className="mt-2 size-2 shrink-0 rotate-45 bg-primary"
                    />
                    <span>{t(b)}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-8 flex flex-wrap gap-3">
                <PrimaryCta to="/registration-demo">Book a demo</PrimaryCta>
                <GhostCta to="/contacts">Talk to a consultant</GhostCta>
              </div>
            </div>
            <img
              src={illustration}
              alt=""
              aria-hidden
              loading="lazy"
              className="mx-auto w-full max-w-sm select-none [transform:rotateX(6deg)_rotateZ(-2deg)] drop-shadow-[0_20px_40px_rgba(0,0,0,0.45)]"
            />
          </div>
        </div>
      </div>
    </section>
  );
}

/** Shared closing call-to-action band used at the bottom of every page. */
export function ClosingCta({
  title = "Your factory deserves better than spreadsheets and guesswork",
}: {
  title?: string;
}) {
  const t = useT();
  return (
    <section className="relative isolate border-t border-border/60 py-20 md:py-28">
      <IsoGrid intensity="strong" />
      <div className="container-page">
        <div className="relative overflow-hidden rounded-2xl border border-primary/30 bg-surface p-10 text-center shadow-[var(--glow-primary)] md:p-16">
          <img
            src={ISO.paperPlane}
            alt=""
            aria-hidden
            loading="lazy"
            className="pointer-events-none absolute -top-6 -right-6 w-32 opacity-40 md:w-44"
          />
          <h2 className="mx-auto max-w-2xl font-display text-3xl font-bold text-balance-tight md:text-4xl">
            {t(title)}
          </h2>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <PrimaryCta to="/registration-demo">Book a demo</PrimaryCta>
            <GhostCta to="/contacts">Talk to a consultant</GhostCta>
          </div>
        </div>
      </div>
    </section>
  );
}

